<template>
  <section class="pdf-lab">
    <header class="pdf-lab__hero">
      <div>
        <span class="pdf-lab__kicker">PDF Lab</span>
        <h1>Pruebas de compras y tickets</h1>
        <p>Escenarios para revisar como se acomodan los PDFs cuando una compra trae varios tickets o cuando el usuario recibe varias compras distintas.</p>
      </div>

      <div class="pdf-lab__actions">
        <button class="pdf-lab__button pdf-lab__button--primary" type="button" @click="generateAllPurchases">
          Generar escenario actual
        </button>
      </div>
    </header>

    <section class="pdf-lab__scenario-grid">
      <button
        v-for="scenario in scenarios"
        :key="scenario.id"
        :class="['pdf-lab__scenario-card', { 'is-active': scenario.id === activeScenarioId }]"
        type="button"
        @click="selectScenario(scenario.id)"
      >
        <strong>{{ scenario.label }}</strong>
        <span>{{ scenario.summary }}</span>
        <small>{{ scenario.description }}</small>
      </button>
    </section>

    <section class="pdf-lab__purchase-grid">
      <article v-for="purchase in activeScenario.purchases" :key="purchase.invoice" class="pdf-lab__purchase-card">
        <div>
          <span class="pdf-lab__purchase-kicker">{{ purchase.label }}</span>
          <h2>{{ purchase.event.name }}</h2>
          <p>{{ purchase.ticketsTable.length }} tickets · orden #{{ purchase.invoice }}</p>
        </div>

        <button class="pdf-lab__button" type="button" @click="generatePurchase(purchase)">
          Generar PDF
        </button>
      </article>
    </section>

    <div class="pdf-lab__notes">
      <p>Escenario activo: <strong>{{ activeScenario.label }}</strong></p>
      <p>Compras: <strong>{{ activeScenario.purchases.length }}</strong> · Tickets totales: <strong>{{ activeScenarioTotalTickets }}</strong></p>
    </div>

    <section v-if="showPreview" class="pdf-lab__preview">
      <div class="pdf-lab__preview-head">
        <span class="pdf-lab__purchase-kicker">Preview visual</span>
        <p>Demo publico con nombre largo para revisar que la direccion no se oculte debajo del titulo.</p>
      </div>

      <PdfGeneration
        v-if="activePreviewPurchase"
        :tickets-table="activePreviewPurchase.ticketsTable"
        :event="activePreviewPurchase.event"
        :invoice="activePreviewPurchase.invoice"
        preview-mode
      />
    </section>

    <div class="pdf-lab__hidden-render">
      <PdfGeneration
        v-if="renderPurchase"
        :key="renderKey"
        :tickets-table="renderPurchase.ticketsTable"
        :event="renderPurchase.event"
        :invoice="renderPurchase.invoice"
        @messageFromPdfGeneration="handleGeneratedPdf"
      />
    </div>
  </section>
</template>

<script>
import PdfGeneration from './PdfGeneration.vue'

const DEMO_EVENT = {
  name: 'México Championship - presentado por Scotia Wealth Management',
  start_date: '2026-05-04T17:32:00-03:00',
  venue: {
    name: 'Club de Golf La Hacienda',
  },
  city: {
    name: 'Ciudad de Mexico',
  },
  banner_url: '',
}

const TICKET_NAME = 'Acceso de cortesía demo'

function buildTicket(index, firstName, lastName) {
  return {
    id: index,
    name: TICKET_NAME,
    first_name: firstName,
    last_name: lastName,
    seat_name: 'Acceso libre',
    access_code_snapshot: `FOMO-DEMO-${index}`,
    is_courtesy: true,
  }
}

function buildPurchase(invoice, names) {
  return {
    invoice,
    label: `Compra ${invoice}`,
    event: DEMO_EVENT,
    ticketsTable: names.map((entry, offset) => buildTicket(`${invoice}-${offset + 1}`, entry.firstName, entry.lastName)),
  }
}

export default {
  name: 'PdfGenerationTest',

  components: {
    PdfGeneration,
  },

  data() {
    const scenarios = [
      {
        id: 'one-purchase',
        label: '1 compra',
        summary: '1 compra · 3 tickets',
        description: 'Una sola orden con varios tickets juntos en el mismo PDF.',
        purchases: [
          buildPurchase(91001, [
            { firstName: 'Gmovievk', lastName: 'Uno' },
            { firstName: 'Gmovievk', lastName: 'Dos' },
            { firstName: 'Gmovievk', lastName: 'Tres' },
          ]),
        ],
      },
      {
        id: 'two-purchases',
        label: '2 compras',
        summary: '2 compras · 5 tickets',
        description: 'Dos ordenes separadas: una de 3 tickets y otra de 2.',
        purchases: [
          buildPurchase(92001, [
            { firstName: 'Gmovievk', lastName: 'Uno' },
            { firstName: 'Gmovievk', lastName: 'Dos' },
            { firstName: 'Gmovievk', lastName: 'Tres' },
          ]),
          buildPurchase(92002, [
            { firstName: 'Gmovievk', lastName: 'Cuatro' },
            { firstName: 'Gmovievk', lastName: 'Cinco' },
          ]),
        ],
      },
      {
        id: 'three-purchases',
        label: '3 compras',
        summary: '3 compras · 5 tickets',
        description: 'Tres ordenes para revisar ritmo visual y consistencia entre PDFs.',
        purchases: [
          buildPurchase(93001, [
            { firstName: 'Gmovievk', lastName: 'Uno' },
            { firstName: 'Gmovievk', lastName: 'Dos' },
          ]),
          buildPurchase(93002, [
            { firstName: 'Gmovievk', lastName: 'Tres' },
            { firstName: 'Gmovievk', lastName: 'Cuatro' },
          ]),
          buildPurchase(93003, [
            { firstName: 'Gmovievk', lastName: 'Cinco' },
          ]),
        ],
      },
      {
        id: 'five-purchases',
        label: '5 compras',
        summary: '5 compras · 5 tickets',
        description: 'Cinco ordenes de un ticket para ver el flujo extremo de envios.',
        purchases: [
          buildPurchase(95001, [{ firstName: 'Gmovievk', lastName: 'Uno' }]),
          buildPurchase(95002, [{ firstName: 'Gmovievk', lastName: 'Dos' }]),
          buildPurchase(95003, [{ firstName: 'Gmovievk', lastName: 'Tres' }]),
          buildPurchase(95004, [{ firstName: 'Gmovievk', lastName: 'Cuatro' }]),
          buildPurchase(95005, [{ firstName: 'Gmovievk', lastName: 'Cinco' }]),
        ],
      },
    ]

    return {
      scenarios,
      activeScenarioId: scenarios[0].id,
      renderPurchase: null,
      renderKey: 0,
      generationQueue: [],
    }
  },

  computed: {
    activeScenario() {
      return this.scenarios.find((scenario) => scenario.id === this.activeScenarioId) || this.scenarios[0]
    },

    activeScenarioTotalTickets() {
      return this.activeScenario.purchases.reduce((total, purchase) => total + purchase.ticketsTable.length, 0)
    },

    activePreviewPurchase() {
      return this.activeScenario.purchases[0] || null
    },

    showPreview() {
      return this.$route && String(this.$route.query.preview || '') === '1'
    },
  },

  methods: {
    selectScenario(scenarioId) {
      this.activeScenarioId = scenarioId
      this.generationQueue = []
      this.renderPurchase = null
    },

    generatePurchase(purchase) {
      this.generationQueue = [purchase]
      this.startNextGeneration()
    },

    generateAllPurchases() {
      this.generationQueue = [...this.activeScenario.purchases]
      this.startNextGeneration()
    },

    startNextGeneration() {
      if (!this.generationQueue.length) {
        this.renderPurchase = null
        return
      }

      this.renderKey += 1
      this.renderPurchase = this.generationQueue.shift()
    },

    handleGeneratedPdf(file) {
      const purchase = this.renderPurchase
      if (file && purchase) {
        const url = window.URL.createObjectURL(file)
        const link = document.createElement('a')
        link.href = url
        link.download = `purchase-${purchase.invoice}.pdf`
        link.click()
        window.URL.revokeObjectURL(url)
      }

      this.$nextTick(() => {
        this.startNextGeneration()
      })
    },
  },
}
</script>

<style scoped>
.pdf-lab {
  min-height: 100vh;
  padding: 32px;
  background:
    radial-gradient(circle at top left, rgba(117, 225, 238, 0.08), transparent 24%),
    radial-gradient(circle at top right, rgba(126, 104, 194, 0.1), transparent 22%),
    linear-gradient(180deg, #09090f, #06070d);
  color: #fff;
}

.pdf-lab__hero,
.pdf-lab__scenario-grid,
.pdf-lab__purchase-grid {
  display: grid;
  gap: 16px;
}

.pdf-lab__hero {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  margin-bottom: 24px;
}

.pdf-lab__kicker,
.pdf-lab__purchase-kicker {
  color: #bfc8db;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.pdf-lab__hero h1,
.pdf-lab__purchase-card h2 {
  margin: 8px 0 0;
}

.pdf-lab__hero p,
.pdf-lab__purchase-card p,
.pdf-lab__notes p,
.pdf-lab__scenario-card small,
.pdf-lab__scenario-card span {
  margin: 0;
  color: rgba(194, 204, 223, 0.84);
  line-height: 1.5;
}

.pdf-lab__actions,
.pdf-lab__purchase-card {
  display: flex;
  gap: 12px;
}

.pdf-lab__button,
.pdf-lab__scenario-card {
  border-radius: 18px;
  border: 1px solid rgba(201, 211, 231, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
}

.pdf-lab__button {
  padding: 12px 18px;
  font-weight: 700;
}

.pdf-lab__button--primary {
  background: linear-gradient(180deg, rgba(126, 104, 194, 0.34), rgba(117, 225, 238, 0.18));
}

.pdf-lab__scenario-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 24px;
}

.pdf-lab__scenario-card {
  display: grid;
  gap: 8px;
  padding: 16px;
  text-align: left;
}

.pdf-lab__scenario-card.is-active {
  border-color: rgba(117, 225, 238, 0.4);
  box-shadow: 0 0 0 1px rgba(117, 225, 238, 0.25) inset;
}

.pdf-lab__purchase-grid {
  margin-bottom: 20px;
}

.pdf-lab__purchase-card {
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(201, 211, 231, 0.1);
  background: rgba(255, 255, 255, 0.03);
}

.pdf-lab__notes {
  display: grid;
  gap: 6px;
  margin-bottom: 16px;
}

.pdf-lab__preview {
  display: grid;
  gap: 16px;
  overflow-x: auto;
  margin-bottom: 24px;
}

.pdf-lab__preview-head {
  display: grid;
  gap: 6px;
}

.pdf-lab__preview-head p {
  margin: 0;
  color: rgba(194, 204, 223, 0.84);
}

.pdf-lab__hidden-render {
  position: fixed;
  left: -99999px;
  top: 0;
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 1200px) {
  .pdf-lab__scenario-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .pdf-lab {
    padding: 20px;
  }

  .pdf-lab__hero,
  .pdf-lab__purchase-card,
  .pdf-lab__scenario-grid {
    grid-template-columns: 1fr;
    display: grid;
  }
}
</style>
